import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AdoptionPlanDto, ChartTemplateInstallDto, ChartTemplatePreviewDto, ChartTemplateSummary, ConfigReference } from "@delta/shared";
import { useLang } from "../../i18n";
import { api, ApiFailure } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { indentPx, indentText } from "../../lib/indent";
import { useSession } from "../../lib/session";
import { Icon } from "../../components/Icon";
import { Confirm } from "../../components/Modal";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";
import { printReport } from "../../print/report";
import { usePrintData } from "../ledger/common";

// Settings › Chart template (migration 037, docs/chart-templates.md). Before anything is installed the owner sees the
// whole template: every entry with its supplied type, level, nature and function, the two documented corrections,
// the template's checks and how each entry meets this organization's chart. Installing adds the groups and accounts
// in one step (the server refuses conflicts, repeats nothing and installs only into this organization). Below: the
// settings that point at accounts, for review (nothing is chosen for the owner), and, where a chart is already in
// use, an adoption plan the owner writes account by account; Delta never replaces a chart in use.

export function ChartTemplate() {
  const { t, lang } = useLang();
  const s = useSession();
  const toast = useToast();
  const [list, setList] = useState<ChartTemplateSummary[] | null>(null);
  const [key, setKey] = useState<string | null>(null);
  const [p, setP] = useState<ChartTemplatePreviewDto | null>(null);
  const [plan, setPlan] = useState<AdoptionPlanDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [ask, setAsk] = useState(false);
  const [alongside, setAlongside] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ChartTemplateInstallDto | null>(null);

  const load = useCallback(async (k?: string) => {
    setFailed(null);
    try {
      const l = await api<ChartTemplateSummary[]>("GET", "/chart-templates");
      setList(l);
      const use = k ?? l[0]?.key ?? null;
      setKey(use);
      if (!use) return;
      const [pv, pl] = await Promise.all([api<ChartTemplatePreviewDto>("GET", `/chart-templates/${use}`), api<AdoptionPlanDto>("GET", `/chart-templates/${use}/plan`)]);
      setP(pv); setPlan(pl);
    } catch (e) { setFailed(errorText(t, e)); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);

  const when = (iso: string) => new Date(iso).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Riyadh" });
  const orgName = s.me ? (lang === "ar" ? s.me.organization.nameAr : s.me.organization.nameEn) : "";
  const name = (e: { nameAr: string; nameEn: string }) => (lang === "ar" ? e.nameAr : e.nameEn);
  const other = (e: { nameAr: string; nameEn: string }) => (lang === "ar" ? e.nameEn : e.nameAr);
  const print = usePrintData();

  async function install() {
    if (!p || !key) return;
    setAsk(false); setBusy(true);
    try {
      const r = await api<ChartTemplateInstallDto>("POST", `/chart-templates/${key}/install`,
        { version: p.template.version, groups: p.template.groups, accounts: p.template.accounts, ...(p.needsAlongside ? { alongsideExisting: alongside } : {}) });
      setDone(r);
      toast(r.status === "installed" ? t("tplInstalledOk", { g: r.groupsCreated, a: r.accountsCreated }) : t("tplAlready"));
      await load(key);
    } catch (e) {
      toast(errorText(t, e));
      if (e instanceof ApiFailure && e.code === "template_changed") await load(key);
    } finally { setBusy(false); }
  }

  function printHierarchy() {
    if (!p) return;
    const cols = [{ label: t("code") }, { label: t("name") }, { label: t("tplType") }, { label: t("tplLevel") }, { label: t("tplNature") }, { label: t("tplRole") }];
    const rows = p.entries.map((e) => ({
      cells: [e.code, `${indentText(e.level - 1, " ")}${name(e)}`, e.typeText, e.levelText, lang === "ar" ? e.natureText : t(`nature_${e.nature}`), t(`role_${e.role}`)],
      kind: e.role === "group" && e.level === 1 ? ("section" as const) : e.role === "group" ? ("subtotal" as const) : undefined
    }));
    void printReport(print(`${t("setChartTemplate")}: ${lang === "ar" ? p.template.titleAr : p.template.titleEn}`, cols, rows, undefined,
      [t("tplSupplied", { d: p.template.suppliedOn, v: p.template.version }), ...p.corrections.map((c) => `${c.code}: ${c.from} → ${c.to}`)]));
  }

  const head = <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setChartTemplate")]]} title={t("setChartTemplate")} />;
  if (failed) return <Shell navKey="/settings/chart-template">{head}<section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p>
    <button className="btn btn-primary" onClick={() => void load(key ?? undefined)}><Icon name="refresh" />{t("retry")}</button></div></section></Shell>;
  if (!list || !p || !plan) return <Shell navKey="/settings/chart-template">{head}<section className="card"><div className="state-box"><p>{t("loading")}</p></div></section></Shell>;

  const inst = p.template.installed;
  const okToInstall = p.canInstall && (!p.needsAlongside || alongside);
  return (
    <Shell navKey="/settings/chart-template">
      {head}
      <div className="set-stack" data-testid="tpl-page">
        <div className="dep-note"><Icon name="info" /><span>{t("tplIntro")}</span></div>

        <section className="set-sec">
          <div className="set-sec-h"><h2 lang="ar" dir="rtl" data-testid="tpl-title">{p.template.titleAr}</h2>
            {list.length > 1 && <select className="control" value={key ?? ""} onChange={(e) => void load(e.target.value)}>{list.map((x) => <option key={x.key} value={x.key}>{lang === "ar" ? x.titleAr : x.titleEn}</option>)}</select>}</div>
          <div className="card set-sec-b">
            <p className="hint">{t("tplSupplied", { d: p.template.suppliedOn, v: p.template.version })}</p>
            <dl className="kv" style={{ ["--kv-cols" as string]: 4 }} data-testid="tpl-summary">
              <div><dt>{t("tplEntries")}</dt><dd className="num">{p.entries.length}</dd></div>
              <div><dt>{t("tplGroups")}</dt><dd className="num">{p.template.groups}</dd></div>
              <div><dt>{t("tplPosting")}</dt><dd className="num">{p.template.accounts}</dd></div>
              <div><dt>{t("tplStatus")}</dt><dd data-testid="tpl-status">{inst ? t("tplInstalledOn", { d: when(inst.installedAt), u: inst.installedBy, g: inst.groupsCreated, a: inst.accountsCreated }) : t("tplNotInstalled")}</dd></div>
              <div style={{ gridColumn: "1 / -1" }}><dt>{t("tplExisting")}</dt><dd data-testid="tpl-existing">{t("tplExistingCounts", { g: p.existing.groups, a: p.existing.accounts, l: p.existing.accountsWithLines, n: p.existing.journalLines })}</dd></div>
            </dl>
            <div className="ob-links" style={{ marginTop: 12 }}>
              {!inst && <button className="btn btn-primary" disabled={!okToInstall || busy} onClick={() => setAsk(true)} data-testid="tpl-install"><Icon name="download" />{t("tplInstall")}</button>}
              {(inst || done) && <Link className="btn btn-primary" to="/chart" data-testid="tpl-open-chart"><Icon name="ledger" />{t("tplOpenChart")}</Link>}
              <button className="btn" onClick={printHierarchy} data-testid="tpl-print"><Icon name="printer" />{t("tplPrint")}</button>
            </div>
            {!inst && p.needsAlongside && p.canInstall && <label className="check" style={{ marginTop: 10 }}><input type="checkbox" checked={alongside} onChange={(e) => setAlongside(e.target.checked)} data-testid="tpl-alongside" />
              <span>{t("tplAlongside", { g: p.existing.groups, a: p.existing.accounts })}</span></label>}
            {!inst && p.blockers.length > 0 && <div className="dep-note warn" style={{ marginTop: 10 }} data-testid="tpl-blockers"><Icon name="lock" /><span><b>{t("tplBlocked")}: </b>{p.blockers.map((b) => t(`blocker_${b}`)).join(" ")}</span></div>}
          </div>
        </section>

        <section className="set-sec">
          <div className="set-sec-h"><h2>{t("tplChecks")}</h2></div>
          {/* A check reads as a rule when it is met and as "Not met: …" when it is not; the conflicts check counts the
              entries and points to the side-by-side list instead of printing internal state names. */}
          <div className="card set-sec-b"><ul className="tpl-checks" data-testid="tpl-checks">{p.checks.map((c) => (
            <li key={c.key} className={c.ok ? "ok" : "bad"} data-check={c.key} data-ok={c.ok}><Icon name={c.ok ? "check" : "alert"} />
              {c.ok ? <span>{t(`check_${c.key}`)}</span> : c.key === "no_conflicts_with_this_organization"
                ? <span>{t("checkConflictsFail", { n: c.detail.length })} · <a href="#tpl-conflicts">{t("checkConflictsSee")}</a></span>
                : <span>{t("checkNotMet", { rule: t(`check_${c.key}`) })}{c.detail.length > 0 && <span className="muted num" dir="ltr"> ({c.detail.slice(0, 12).join(", ")}{c.detail.length > 12 ? "…" : ""})</span>}</span>}</li>))}</ul></div>
        </section>

        {!inst && <Conflicts p={p} title={lang === "ar" ? p.template.titleAr : p.template.titleEn} />}

        <section className="set-sec">
          <div className="set-sec-h"><h2>{t("tplCorrections")}</h2></div>
          <div className="card set-sec-b">
            <p className="hint">{t("tplCorrectionsNote")}</p>
            <div className="table-wrap"><table className="tbl" data-testid="tpl-corrections">
              <thead><tr><th>{t("code")}</th><th>{t("tplField")}</th><th>{t("tplSuppliedValue")}</th><th>{t("tplCorrected")}</th><th>{t("tplReason")}</th></tr></thead>
              <tbody>{p.corrections.map((c) => <tr key={c.code}><td className="num" dir="ltr">{c.code}</td><td>{t(`tplField_${c.field}`)}</td><td lang="ar" dir="auto"><s>{c.from}</s></td><td lang="ar" dir="rtl">{c.to}</td><td className="muted" lang="en" dir="ltr">{c.reason}</td></tr>)}</tbody>
            </table></div>
          </div>
        </section>

        <section className="set-sec">
          <div className="set-sec-h"><h2>{t("tplHierarchy")}</h2></div>
          <div className="card set-sec-b">
            <p className="hint">{t("tplHierarchyNote")} {t("tplNameEnNote")}</p>
            <div className="table-wrap"><table className="tbl dense tpl-tree" data-testid="tpl-hierarchy">
              <thead><tr><th>{t("code")}</th><th>{t("name")}</th><th>{t("tplType")}</th><th>{t("tplLevel")}</th><th>{t("tplNature")}</th><th>{t("tplRole")}</th><th>{t("tplState")}</th></tr></thead>
              <tbody>{p.entries.map((e) => (
                <tr key={e.code} className={e.role === "group" ? "tpl-group" : ""} data-code={e.code} data-role={e.role} data-state={e.state}>
                  <td className="num" dir="ltr">{e.code}</td>
                  <td style={{ paddingInlineStart: indentPx(e.level - 1, 16, 8) }}>{e.role === "group" ? <b>{name(e)}</b> : name(e)}<div className="muted" style={{ fontSize: 11 }}>{other(e)}</div></td>
                  <td lang="ar" dir="auto">{e.typeText}</td>
                  <td className="num">{e.level}</td>
                  <td>{t(`nature_${e.nature}`)}</td>
                  <td><span className={`pill ${e.role === "group" ? "" : "good"}`}>{t(`role_${e.role}`)}</span></td>
                  <td>{e.state === "new" || e.state === "installed" ? <span className="muted">{t(`state_${e.state}`)}</span>
                    : <span className="pill bad" title={e.conflict ? `${e.conflict.code ?? ""} ${name(e.conflict)}` : ""}>{t(`state_${e.state}`)}{e.conflict?.code && e.state !== "code_released" ? ` ${t("tplConflictWith", { x: `${e.conflict.code} ${name(e.conflict)}` })}` : ""}</span>}</td>
                </tr>))}</tbody>
            </table></div>
          </div>
        </section>

        <References refs={p.existing.references} />
        {plan.unmapped.length + plan.mappings.length > 0 && <Plan k={key!} plan={plan} entries={p.entries} onSaved={setPlan} owner={!!s.me?.user.owner} />}
      </div>
      {ask && <Confirm title={t("tplInstall")} body={t("tplInstallConfirm", { g: p.template.groups, a: p.template.accounts, o: orgName })} okLabel={t("tplInstall")}
        onOk={() => void install()} onClose={() => setAsk(false)} />}
    </Shell>
  );
}

type Entry = ChartTemplatePreviewDto["entries"][number];
type Named2 = { code: string | null; nameAr: string; nameEn: string };

/**
 * Each template entry that meets this organization's chart, beside what it meets: code, name, role and the groups
 * above, for the template entry and for the existing group or account, with what the conflict means and what the
 * owner can do next. Read only: nothing here changes, merges, renumbers or installs anything.
 */
function Conflicts({ p, title }: { p: ChartTemplatePreviewDto; title: string }) {
  const { t, lang } = useLang();
  const print = usePrintData();
  const rows = p.entries.filter((e) => e.state !== "new" && e.state !== "installed");
  const byCode = useMemo(() => new Map(p.entries.map((e) => [e.code, e])), [p.entries]);
  if (!rows.length) return null;
  const nm = (x: { nameAr: string; nameEn: string }) => (lang === "ar" ? x.nameAr : x.nameEn);
  const label = (x: Named2) => `${x.code ?? ""} ${nm(x)}`.trim();
  const tplPath = (e: Entry): Named2[] => {
    const out: Named2[] = [];
    for (let c = e.parentCode; c && out.length < 32; c = byCode.get(c)?.parentCode ?? null) { const g = byCode.get(c); if (!g) break; out.unshift(g); }
    return out;
  };
  const path = (xs: Named2[]) => (xs.length ? xs.map(label).join(" › ") : t("tplTopLevel"));
  const meaning = (e: Entry) => t(`conflict_${e.state}`, { k: e.conflict && e.conflict.kind !== "released" ? t(`kind_${e.conflict.kind}`) : "" });
  const existingRole = (e: Entry) => (e.conflict?.kind === "group" ? t("role_group") : e.conflict?.kind === "account" ? t("role_posting") : "–");
  function printIt() {
    const cols = [{ label: t("tplConflictTemplate") }, { label: t("tplConflictPath") }, { label: t("tplConflictExisting") }, { label: t("tplConflictPath") }, { label: t("tplConflictMeaning") }];
    const body = rows.map((e) => ({ cells: [`${label(e)} · ${t(`role_${e.role}`)}`, path(tplPath(e)),
      e.conflict && e.conflict.kind !== "released" ? `${label(e.conflict)} · ${existingRole(e)}` : t("tplNoRecord"), e.conflict ? path(e.conflict.path) : "–", meaning(e)] }));
    void printReport(print(`${t("tplConflicts")}: ${title}`, cols, body, undefined, [t("tplConflictsIntro"), t("tplNext4")]));
  }
  const side = (code: string | null, name: string, role: string, under: string, testid: string) => (
    <dl className="tpl-side" data-testid={testid}>
      <div><dt>{t("tplConflictCode")}</dt><dd className="num" dir="ltr">{code ?? "–"}</dd></div>
      <div><dt>{t("tplConflictName")}</dt><dd>{name}</dd></div>
      <div><dt>{t("tplConflictRole")}</dt><dd>{role}</dd></div>
      <div><dt>{t("tplConflictPath")}</dt><dd className="tpl-path">{under}</dd></div>
    </dl>);
  return (
    <section className="set-sec" id="tpl-conflicts" data-testid="tpl-conflicts">
      <div className="set-sec-h"><h2>{t("tplConflicts")} <span className="pill bad num">{rows.length}</span></h2>
        <button className="btn btn-sm" onClick={printIt} data-testid="tpl-conflicts-print"><Icon name="printer" />{t("tplPrintConflicts")}</button></div>
      <div className="card set-sec-b">
        <div className="dep-note warn"><Icon name="alert" /><span>{t("tplConflictsIntro")}</span></div>
        <div className="table-wrap"><table className="tbl tpl-conf" data-testid="tpl-conflict-table">
          <thead><tr><th>{t("tplConflictTemplate")}</th><th>{t("tplConflictExisting")}</th><th>{t("tplConflictMeaning")}</th></tr></thead>
          <tbody>{rows.map((e) => (
            <tr key={e.code} data-code={e.code} data-state={e.state}>
              <td>{side(e.code, nm(e), t(`role_${e.role}`), path(tplPath(e)), "tpl-conf-template")}</td>
              <td>{e.conflict && e.conflict.kind !== "released" ? side(e.conflict.code, nm(e.conflict), existingRole(e), path(e.conflict.path), "tpl-conf-existing")
                : <span className="muted" data-testid="tpl-conf-existing">{t("tplNoRecord")}</span>}</td>
              <td><span className="pill bad">{t(`state_${e.state}`)}</span><p className="tpl-meaning" data-testid="tpl-conf-meaning">{meaning(e)}</p></td>
            </tr>))}</tbody>
        </table></div>
        <div className="tpl-next" data-testid="tpl-next"><h3>{t("tplNextTitle")}</h3>
          <ol><li>{t("tplNext1")}</li><li>{t("tplNext2")}</li><li>{t("tplNext3")}</li><li>{t("tplNext4")}</li></ol></div>
      </div>
    </section>
  );
}

function References({ refs }: { refs: ConfigReference[] }) {
  const { t, L } = useLang();
  return (
    <section className="set-sec">
      <div className="set-sec-h"><h2>{t("tplRefs")}</h2></div>
      <div className="card set-sec-b">
        <div className="dep-note"><Icon name="info" /><span>{t("tplRefsNote")}</span></div>
        <div className="table-wrap"><table className="tbl dense" data-testid="tpl-refs">
          <thead><tr><th>{t("tplSetting")}</th><th>{t("tplRecord")}</th><th>{t("tplAccount")}</th><th>{t("tplWhere")}</th></tr></thead>
          <tbody>{refs.map((r, i) => (
            <tr key={i} data-setting={r.setting}>
              <td>{t(`ref_${r.setting}`)}{r.field && <span className="muted"> · {t(`reff_${r.field}`)}</span>}</td>
              <td>{r.record ? L(r.record) : "–"}</td>
              <td>{r.account ? <><span className="num" dir="ltr">{r.account.code}</span> {L(r.account)}{r.fromTemplate && <span className="pill" style={{ marginInlineStart: 6 }}>{t("tplFromTemplate")}</span>}</> : <span className="muted">{t("tplNotSet")}</span>}</td>
              <td><Link to={r.screen}>{t("tplOpen")}</Link></td>
            </tr>))}</tbody>
        </table></div>
      </div>
    </section>
  );
}

function Plan({ k, plan, entries, onSaved, owner }: { k: string; plan: AdoptionPlanDto; entries: ChartTemplatePreviewDto["entries"]; onSaved(p: AdoptionPlanDto): void; owner: boolean }) {
  const { t, L, lang } = useLang();
  const toast = useToast();
  const posting = useMemo(() => entries.filter((e) => e.role === "posting"), [entries]);
  const [pick, setPick] = useState<Record<string, string>>(() => Object.fromEntries(plan.mappings.map((m) => [m.account.id, m.templateCode])));
  const [note, setNote] = useState(plan.note);
  const [busy, setBusy] = useState(false);
  const rows = [...plan.mappings.map((m) => ({ ...m.account, references: 0, mapped: true })), ...plan.unmapped.map((u) => ({ ...u, mapped: false }))];
  async function save() {
    setBusy(true);
    try {
      const mappings = Object.entries(pick).filter(([, c]) => c).map(([accountId, templateCode]) => ({ accountId, templateCode }));
      onSaved(await api<AdoptionPlanDto>("PUT", `/chart-templates/${k}/plan`, { mappings, note }));
      toast(t("tplPlanSaved"));
    } catch (e) { toast(errorText(t, e)); } finally { setBusy(false); }
  }
  const b = plan.replacement.blockedBy;
  return (
    <section className="set-sec">
      <div className="set-sec-h"><h2>{t("tplPlan")}</h2>{owner && <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void save()} data-testid="tpl-plan-save">{t("tplPlanSave")}</button>}</div>
      <div className="card set-sec-b">
        <p className="hint">{t("tplPlanNote")}</p>
        <div className="dep-note warn" data-testid="tpl-replace-blocked"><Icon name="lock" /><span>{t("tplReplaceBlocked", { a: b.accountsWithLines, n: b.journalLines, r: b.references })}</span></div>
        {!owner && <p className="hint">{t("tplPlanOwnerOnly")}</p>}
        {plan.updatedAt && <p className="hint">{t("tplPlanBy", { d: plan.updatedAt.slice(0, 16).replace("T", " "), u: plan.updatedBy ?? "" })}</p>}
        <div className="table-wrap"><table className="tbl dense" data-testid="tpl-plan">
          <thead><tr><th>{t("code")}</th><th>{t("tplAccount")}</th><th className="end">{t("tplPlanLines")}</th><th>{t("tplPlanFor")}</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} data-account={r.code}>
              <td className="num" dir="ltr">{r.code}</td><td>{L(r)}</td><td className="end num">{r.journalLines}</td>
              <td><select className="control" style={{ minWidth: 220 }} disabled={!owner} value={pick[r.id] ?? ""} aria-label={`${t("tplPlanFor")}: ${r.code}`}
                onChange={(e) => setPick((o) => ({ ...o, [r.id]: e.target.value }))}>
                <option value="">{t("tplPlanNone")}</option>{posting.map((e) => <option key={e.code} value={e.code}>{e.code} {lang === "ar" ? e.nameAr : e.nameEn}</option>)}
              </select></td>
            </tr>))}</tbody>
        </table></div>
        <label className="field" style={{ marginTop: 10 }}><span>{t("tplPlanNote2")}</span>
          <textarea className="control" rows={2} maxLength={2000} disabled={!owner} value={note} onChange={(e) => setNote(e.target.value)} /></label>
      </div>
    </section>
  );
}
